// ─────────────────────────────────────────────────────────────
// THE ECOM BIBLE — centralized editable content configuration
// Update price, checkout URL, stats, author photo, testimonials,
// bonuses and FAQs here. Components read from this file only.
// ─────────────────────────────────────────────────────────────

export const config = {
  brand: {
    name: "The E-Com Factory",
    domain: "theecomfactory.com",
    url: "https://www.theecomfactory.com",
    tagline: "We help brands scale",
  },
  book: {
    title: "THE ECOM BIBLE",
    coverImage: "/images/book-cover.webp",
    pdfUrl: "/book/the-ecom-bible.pdf?v=14",
    price: null, // ← set when confirmed, e.g. "$47". Displayed only when set.
    checkoutUrl: "#get-the-book", // ← replace with the live checkout URL (https://...)
    format: "Digital book",
    note: "Free Instant digital access • Read on any device",
  },
  cta: {
    primary: "GET THE ECOM BIBLE",
  },
  author: {
    name: "ANTHONY DOUGLAS-IZEAR",
    role: "Entrepreneur • Ecommerce Operator • Founder",
    photo: "/images/anthony-seated.webp",
    photoAlt: "Portrait of Anthony Douglas-Izear",
  },
  // Social proof — values stay null until verified figures are supplied.
  stats: [
    { value: "Four", label: "7 Figure Businesses Built" },
    { value: "18", label: "Years in Ecommerce" },
    { value: "109", label: "Entrepreneurs Helped" },
    { value: "Three", label: "Students Taken From 0 to 7 Figures" },
  ],
  noise: [
    "TikTok strategies",
    "YouTube tutorials",
    "Shopify hacks",
    "Meta Ads tactics",
    "AI tools",
    "Email strategies",
    "Product research software",
    "Landing page advice",
  ],
  bts: [
    {
      num: "01",
      title: "BUILD IT.",
      tagline: "Build something worth scaling.",
      topics: [
        "Finding opportunities",
        "Understanding demand",
        "Product selection",
        "Customer psychology",
        "Market research",
        "Positioning",
        "Branding",
        "Website strategy",
        "Offer creation",
      ],
    },
    {
      num: "02",
      title: "TEST IT.",
      tagline: "Let the market tell you what's working.",
      topics: [
        "Launching",
        "Testing offers",
        "Testing messaging",
        "Testing creative",
        "Customer feedback",
        "Reading data",
        "Finding product-market fit",
        "Improving conversion",
      ],
    },
    {
      num: "03",
      title: "SCALE IT.",
      tagline: "Put resources behind what has already proven itself.",
      topics: [
        "Traffic",
        "Paid advertising",
        "Customer acquisition",
        "Conversion optimization",
        "Average order value",
        "Retention",
        "Email",
        "Operations",
        "Systems",
        "Team",
        "Profitability",
      ],
    },
  ],
  frameworks: [
    {
      name: "THE BTS METHOD™",
      explanation:
        "The operating order for the entire business — build something worth scaling, let the market validate it, then put resources behind what has proven itself.",
      takeaway: "Never scale what you haven't tested.",
    },
    {
      name: "THE OPPORTUNITY FILTER",
      explanation:
        "A screen for evaluating product ideas against demand, margin, and your actual ability to reach the buyer — before you spend a dollar.",
      takeaway: "Kill weak ideas on paper, not in your bank account.",
    },
    {
      name: "EVERY PAGE HAS ONE JOB",
      explanation:
        "Each page of your store exists to move the visitor one step forward. Anything that doesn't serve that job gets removed.",
      takeaway: "Confused visitors don't buy.",
    },
    {
      name: "THE OFFER EQUATION",
      explanation:
        "Why people buy: perceived value versus price versus risk. Strengthen the value, lower the risk, and price stops being the objection.",
      takeaway: "Compete on the offer, not on discounts.",
    },
    {
      name: "FEATURES VS. TRANSFORMATIONS",
      explanation:
        "Customers don't buy what the product is — they buy who they become after using it. Write to the transformation.",
      takeaway: "Sell the after, not the item.",
    },
    {
      name: "TRAFFIC IS A MAGNIFIER",
      explanation:
        "Paid traffic doesn't fix a weak store — it exposes it faster. Magnify a converting offer, not a guess.",
      takeaway: "Fix conversion before you buy clicks.",
    },
    {
      name: "THE JOURNEY AFTER CHECKOUT",
      explanation:
        "The sale is the midpoint, not the finish. Retention, email, and experience turn one order into a business.",
      takeaway: "Profit is built after the first purchase.",
    },
    {
      name: "WHO SHOULD OWN THIS TASK?",
      explanation:
        "A delegation filter for deciding what you do, what you systemize, and what you hand off as the business grows.",
      takeaway: "Systems scale. Hustle doesn't.",
    },
  ],
  journey: [
    "FIND THE OPPORTUNITY",
    "UNDERSTAND THE CUSTOMER",
    "BUILD THE PRODUCT",
    "CREATE THE BRAND",
    "BUILD THE WEBSITE",
    "CRAFT THE OFFER",
    "LAUNCH",
    "TEST",
    "GET TRAFFIC",
    "CONVERT CUSTOMERS",
    "BUILD RETENTION",
    "CREATE SYSTEMS",
    "SCALE",
  ],
  forYou: [
    "You have an idea but don't know where to start.",
    "You've been researching ecommerce for months but haven't launched.",
    "You launched a store but sales aren't consistent.",
    "You're spending money on ads without understanding what is actually broken.",
    "You're constantly changing products instead of fixing the underlying business.",
    "You feel overwhelmed by conflicting ecommerce advice.",
    "You want to build a real brand instead of chasing random trending products.",
    "You want a framework you can return to whenever you feel stuck.",
  ],
  notForYou: [
    "You're looking for overnight riches.",
    "You want someone to hand you a secret winning product.",
    "You aren't willing to test.",
    "You expect your first attempt to be perfect.",
    "You want shortcuts instead of skills.",
  ],
  founder: {
    paragraphs: [
      { text: "Anthony didn't come from money or connections." },
      { text: "He came from humble beginnings in St. Louis, Missouri." },
      { text: "By the time he left home at 17, he had lived in 38 different places.", emphasis: true },
      { text: "He learned early that if he wanted to change his circumstances, he would have to create the opportunity himself.", emphasis: true },
      { text: "Years later, after putting himself through college and earning degrees in Business Management and Accounting from the University of South Florida, Anthony discovered ecommerce—and found the vehicle that would change the trajectory of his life." },
      { text: "But there was no roadmap.", emphasis: true },
      { text: "So he learned by doing." },
      { text: "Building brands. Launching products. Running ads. Making expensive mistakes. Testing ideas. Finding winners. Building teams. Creating systems. Scaling." },
      { text: "That experience eventually led to multiple seven-figure businesses—and taught him one lesson that would change the way he thought about ecommerce:" },
    ],
    lesson: ["You don't need to know everything.", "You need to know what to do next."],
    bridge: "That idea became The Ecom Bible and the foundation of the BTS Method™:",
    method: "BUILD IT. TEST IT. SCALE IT.",
  },
  valueStack: [
    "Complete digital book",
    "The BTS Method™",
    "Ecommerce frameworks",
    "Practical exercises",
    "Actionable implementation steps",
    "Read on desktop, tablet, or phone",
    "Instant access",
  ],
  // Real reader results only. Add { quote, name, role } entries as collected.
  testimonials: [
    {
      quote:
        "As someone with experience in e-commerce, I found this book to be a strong reminder that success still comes back to mastering the fundamentals. It does a great job grounding the reader in the core concepts of building, testing, branding, creating value, and scaling without overcomplicating the process. Even if you've been in e-commerce for years, there are principles here worth revisiting because they're easy to lose sight of as the business grows. It's a practical, accessible foundation for both new entrepreneurs and experienced operators who want to stay focused on what actually matters.",
      name: "Brantley Kendall",
    },
    {
      quote:
        "The Ecom Bible cuts through the overwhelm of starting an online business. Anthony's Build It, Test It, Scale It approach gives you a clear path from idea to action. If you've been waiting for everything to be perfect before launching, this book is the push you need.",
      name: "Jeph Cange",
    },
    {
      quote:
        "An attractive website doesn't automatically make a successful business. The Ecom Bible brings the focus back to what matters: understanding your customer, proving demand, and building something worth scaling. Anthony delivers practical lessons in a direct, approachable way that makes you want to get to work.",
      name: "Adam Schiffman",
    },
  ],
  faqs: [
    {
      q: "Who is The Ecom Bible for?",
      a: "Anyone building — or trying to build — a real ecommerce business. Whether you're pre-launch, sitting on an idea, or running a store that isn't converting consistently, the book organizes the work in the correct order.",
    },
    {
      q: "Is this for beginners?",
      a: "Yes. It starts from identifying an opportunity and assumes no existing store. Operators with live stores use the same framework to diagnose what's actually broken.",
    },
    {
      q: "Will this help if I already have an ecommerce store?",
      a: "Yes. The Test It and Scale It phases cover offers, conversion, traffic, retention, and systems — written for stores that are launched but stuck.",
    },
    {
      q: "Is this a physical book or a digital book?",
      a: "A digital book. You get instant access after purchase and can read it on desktop, tablet, or phone.",
    },
    {
      q: "How do I access the book?",
      a: "Immediately after checkout you'll receive access by email. No shipping, no waiting.",
    },
    {
      q: "Does this teach paid advertising?",
      a: "Yes — deliberately late in the process, in the Scale It phase. Advertising magnifies what's already working, so the book first shows you what must be true before you spend on traffic.",
    },
    {
      q: "Does the book tell me what product to sell?",
      a: "No — and be skeptical of anything that does. It gives you the Opportunity Filter and a research process to find and validate your own product.",
    },
    {
      q: "How is this different from free ecommerce content online?",
      a: "Free content tells you what to do, in random order. The Ecom Bible is one organized system: what to do first, what to do next, and what to ignore for now.",
    },
    {
      q: "Do I need a lot of money to start ecommerce?",
      a: "No. The Build and Test phases are designed to validate demand before significant spend. You'll invest in small tests, not guesses.",
    },
    {
      q: "Is this another dropshipping book?",
      a: "No. It's about building a real brand and a durable business — product, offer, customer, retention, and systems — not chasing trending products.",
    },
  ],
  nav: [
    { label: "What's Inside", href: "#frameworks" },
    { label: "BTS Method", href: "#method" },
    { label: "About Anthony", href: "#founder" },
    { label: "FAQ", href: "#faq" },
  ],
  footer: {
    disclaimer:
      "The Ecom Bible teaches process, not promises. Individual results depend on the work you put in — no income is guaranteed.",
  },
};
